import React, { useEffect } from "react";
import { CustomModal } from "@/components/Modal";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { convertTypeEnglishToJapanese } from "../utils";

interface RemarksFormData {
  remarks: string;
}

interface RemarksDetailModalProps {
  open: boolean;
  onClose: () => void;
  rowData: {
    id: number;
    bookType: string;
    location: string;
    chibanKaokuNumber: string;
    remarks?: string;
  } | null;
  onSave: (id: number, remarks: string) => void;
}

export const RemarksDetailModal: React.FC<RemarksDetailModalProps> = ({
  open,
  onClose,
  rowData,
  onSave,
}) => {
  const { register, reset, handleSubmit, watch } = useForm<RemarksFormData>({
    defaultValues: {
      remarks: "",
    },
  });

  const remarksValue = watch("remarks");

  useEffect(() => {
    if (open && rowData) {
      // モーダルが開いた時に必ずリセット
      reset({ remarks: rowData.remarks ?? "" });
    } else if (!open) {
      // モーダルが閉じた時にクリア
      reset({ remarks: "" });
    }
  }, [rowData?.id, rowData?.remarks, open, reset]);

  const onSubmit = (data: RemarksFormData): void => {
    if (rowData) {
      onSave(rowData.id, data.remarks);
      reset({ remarks: "" }); // フォームをクリア
      onClose();
    }
  };

  const handleClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason === "backdropClick") {
      return;
    }
    reset({ remarks: "" }); // フォームをクリア
    onClose();
  };

  const handleCancel = (): void => {
    reset({ remarks: "" }); // フォームをクリア
    onClose();
  };

  if (!rowData) {
    return null;
  }

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleClose}
      ariaLabelledby="remarks-detail-modal-title"
      ariaDescribedby="remarks-detail-modal-description"
      width={600}
      overflow="auto"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit(onSubmit)(e);
        }}
      >
        <Stack spacing={3}>
          <Typography
            id="remarks-detail-modal-title"
            variant="h5"
            component="h2"
          >
            備考詳細
          </Typography>

          <Box>
            <Typography variant="body2" color="text.secondary">
              種別: {convertTypeEnglishToJapanese(rowData.bookType)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              所在: {rowData.location}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              地番/家屋番号: {rowData.chibanKaokuNumber}
            </Typography>
          </Box>

          <Box>
            <TextField
              {...register("remarks")}
              label="備考"
              multiline
              rows={4}
              fullWidth
              inputProps={{ maxLength: 200 }}
              helperText={`${remarksValue?.length || 0}/200文字`}
            />
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleCancel} size="large">
              キャンセル
            </Button>
            <Button variant="contained" type="submit" size="large">
              保存
            </Button>
          </Stack>
        </Stack>
      </form>
    </CustomModal>
  );
};
